// frontend/packages/business-console/src/pages/Quotes/CustomerPicker.jsx
//
// Typeahead customer picker with inline-create row. Design doc §3.8.
//
// Behaviour:
//   - User types → debounced search against listCustomers({ q })
//   - Existing-customer match → click to select
//   - "+ Add new customer" row at the bottom of the dropdown → expands
//     into a small form (first / last / email / phone) that POSTs to
//     createCustomer and auto-selects the new customer
//   - Selecting a customer collapses the form back to a chip-style
//     summary with "Change" button

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';

import { listCustomers, createCustomer } from '../../services/crmService';

function customerDisplay(c) {
  if (!c) return '';
  if (c.companyName) return c.companyName;
  const fn = c.firstName || '';
  const ln = c.lastName || '';
  return `${fn} ${ln}`.trim() || 'Customer';
}

export default function CustomerPicker({ value, onChange, disabled, isDemo }) {
  // `value` is either null or the selected customer object.
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createMode, setCreateMode] = useState(false);

  // Inline-create form state
  const [newFirst, setNewFirst] = useState('');
  const [newLast, setNewLast] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query.trim()), 250);
    return () => clearTimeout(id);
  }, [query]);

  // Fetch whenever the search UI is visible — that's when there's no
  // selection yet (a fresh picker: new estimate/invoice, or the D22 form
  // step-0 picker) OR the user hit "Change" on an existing selection. The
  // old guard only fetched on `open`, so a fresh picker (value == null,
  // open == false) never searched and always showed "No matches".
  useEffect(() => {
    const searching = (!value || open) && !createMode && !isDemo;
    if (!searching) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await listCustomers(
          debouncedQuery ? { q: debouncedQuery, limit: 25 } : { limit: 25 }
        );
        if (!cancelled) setItems(res?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [value, open, createMode, isDemo, debouncedQuery]);

  if (value && !open) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ minWidth: 0 }}
        >
          <PersonOutlineRoundedIcon sx={{ color: 'text.secondary' }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 600, color: 'text.primary' }}>
              {customerDisplay(value)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', display: 'block' }}
            >
              {value.email || ''}
              {value.email && value.phone ? '  ·  ' : ''}
              {value.phone || ''}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          startIcon={<EditOutlinedIcon />}
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          Change
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          display: 'block',
          mb: 1,
        }}
      >
        Bill to
      </Typography>

      {!createMode ? (
        <>
          <TextField
            inputRef={inputRef}
            fullWidth
            autoFocus
            size="small"
            placeholder="Search name, email, phone…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={disabled}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: loading ? (
                <InputAdornment position="end">
                  <CircularProgress size={14} />
                </InputAdornment>
              ) : null,
            }}
          />
          {isDemo ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              Customer search is disabled in demo mode. Use the inline-add form
              below to attach a stand-in customer.
            </Alert>
          ) : (
            <List
              dense
              sx={{
                mt: 1,
                maxHeight: 280,
                overflowY: 'auto',
                border: items.length > 0 ? '1px solid' : 'none',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {items.map((c) => (
                <ListItemButton
                  key={c._id}
                  onClick={() => {
                    onChange(c);
                    setOpen(false);
                    setQuery('');
                  }}
                >
                  <Stack>
                    <Typography sx={{ fontWeight: 600 }}>
                      {customerDisplay(c)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: 'text.secondary' }}
                    >
                      {c.email || ''}
                      {c.email && c.phone ? '  ·  ' : ''}
                      {c.phone || ''}
                    </Typography>
                  </Stack>
                </ListItemButton>
              ))}
              {items.length === 0 && debouncedQuery && !loading ? (
                <Box sx={{ px: 2, py: 1.5, color: 'text.secondary' }}>
                  <Typography variant="caption">
                    No matches. Add a new customer below.
                  </Typography>
                </Box>
              ) : null}
            </List>
          )}
          <Button
            fullWidth
            size="small"
            startIcon={<PersonAddAlt1OutlinedIcon />}
            onClick={() => setCreateMode(true)}
            sx={{ mt: 1 }}
            disabled={disabled}
          >
            Add new customer
          </Button>
        </>
      ) : (
        <>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                label="First name"
                value={newFirst}
                onChange={(e) => setNewFirst(e.target.value)}
                disabled={creating}
                required
              />
              <TextField
                fullWidth
                size="small"
                label="Last name"
                value={newLast}
                onChange={(e) => setNewLast(e.target.value)}
                disabled={creating}
                required
              />
            </Stack>
            <TextField
              fullWidth
              size="small"
              type="email"
              label="Email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={creating}
              required
            />
            <TextField
              fullWidth
              size="small"
              label="Phone (optional)"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              disabled={creating}
            />
            {createError ? <Alert severity="error">{createError}</Alert> : null}
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={() => {
                  setCreateMode(false);
                  setCreateError(null);
                }}
                disabled={creating}
              >
                Back to search
              </Button>
              <Button
                size="small"
                variant="contained"
                disabled={creating || !newFirst || !newLast || !newEmail}
                onClick={async () => {
                  setCreating(true);
                  setCreateError(null);
                  if (isDemo) {
                    const stub = {
                      _id: `demo_new_${Date.now()}`,
                      firstName: newFirst,
                      lastName: newLast,
                      email: newEmail,
                      phone: newPhone,
                    };
                    setCreating(false);
                    onChange(stub);
                    setOpen(false);
                    return;
                  }
                  try {
                    const created = await createCustomer({
                      firstName: newFirst,
                      lastName: newLast,
                      email: newEmail,
                      phone: newPhone || undefined,
                    });
                    onChange(created);
                    setOpen(false);
                  } catch (err) {
                    setCreateError(
                      err?.response?.data?.message ||
                        err?.message ||
                        "Couldn't create customer."
                    );
                  } finally {
                    setCreating(false);
                  }
                }}
                startIcon={creating ? <CircularProgress size={14} /> : null}
              >
                {creating ? 'Saving…' : 'Add customer'}
              </Button>
            </Stack>
          </Stack>
        </>
      )}
    </Paper>
  );
}

CustomerPicker.displayChip = function CustomerChip({ customer }) {
  // Compact "chip-style" inline display, used when the picker is
  // collapsed but the parent doesn't want the full Paper card.
  return (
    <Chip
      icon={<PersonOutlineRoundedIcon />}
      label={customerDisplay(customer)}
      sx={{ fontWeight: 600 }}
    />
  );
};
